import React, { useEffect, useState, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import UserDashboard from 'layouts/UserDashboard';
import Container from 'components/Container';
import {
  getBankAccounts,
  deleteBankAccount,
  importCSV,
} from './FinancialService';
import { BankAccountResponse, CSVImportRow } from './types';

const C = {
  ink: '#17263d',
  muted: '#78869a',
  blue: '#1475d1',
  green: '#159b72',
  red: '#ff454d',
  orange: '#ff8b32',
};

const fmt = (n: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 2,
  }).format(n);

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  CURRENT: 'Current',
  SAVINGS: 'Savings',
  CREDIT_CARD: 'Credit Card',
  INVESTMENT: 'Investment',
};

const ACCOUNT_TYPE_COLORS: Record<string, string> = {
  CURRENT: '#1475d1',
  SAVINGS: '#159b72',
  CREDIT_CARD: '#ff8b32',
  INVESTMENT: '#8B5CF6',
};

const SYNC_STATUS_LABELS: Record<
  string,
  { label: string; color: 'success' | 'warning' | 'error' | 'default' }
> = {
  SYNCED: { label: 'Synced', color: 'success' },
  SYNCING: { label: 'Syncing…', color: 'default' },
  STALE: { label: 'Stale data', color: 'warning' },
  NEEDS_REAUTH: { label: 'Needs reauth', color: 'warning' },
  FAILED: { label: 'Sync failed', color: 'error' },
};

const CATEGORIES: Record<
  'DEBIT' | 'CREDIT',
  {
    label: string;
    value: string;
    subCats: { label: string; value: string }[];
  }[]
> = {
  DEBIT: [
    {
      label: 'Needs',
      value: 'NEEDS',
      subCats: [
        { label: 'Housing', value: 'HOUSING' },
        { label: 'Utilities', value: 'UTILITIES' },
        { label: 'Groceries', value: 'GROCERIES' },
        { label: 'Transport', value: 'TRANSPORT' },
        { label: 'Healthcare', value: 'HEALTHCARE' },
        { label: 'Insurance', value: 'INSURANCE' },
        { label: 'Council Tax', value: 'COUNCIL_TAX' },
        { label: 'Childcare', value: 'CHILDCARE' },
        { label: 'Other', value: 'NEEDS_OTHER' },
      ],
    },
    {
      label: 'Wants',
      value: 'WANTS',
      subCats: [
        { label: 'Dining', value: 'DINING' },
        { label: 'Entertainment', value: 'ENTERTAINMENT' },
        { label: 'Shopping', value: 'SHOPPING' },
        { label: 'Subscriptions', value: 'SUBSCRIPTIONS' },
        { label: 'Personal Care', value: 'PERSONAL_CARE' },
        { label: 'Travel', value: 'TRAVEL' },
        { label: 'Other', value: 'WANTS_OTHER' },
      ],
    },
    {
      label: 'Savings',
      value: 'SAVINGS',
      subCats: [
        { label: 'Savings', value: 'SAVINGS' },
        { label: 'Investments', value: 'INVESTMENTS' },
        { label: 'Pension', value: 'PENSION' },
        { label: 'Emergency Fund', value: 'EMERGENCY_FUND' },
        { label: 'Other', value: 'SAVINGS_OTHER' },
      ],
    },
  ],
  CREDIT: [
    { label: 'Salary', value: 'SALARY', subCats: [] },
    { label: 'Other Income', value: 'INCOME_OTHER', subCats: [] },
  ],
};

// ─── CSV helpers ──────────────────────────────────────────────────────────────

function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  for (const line of lines) {
    const cols: string[] = [];
    let inQuote = false;
    let cur = '';
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        inQuote = !inQuote;
      } else if (ch === ',' && !inQuote) {
        cols.push(cur.trim());
        cur = '';
      } else {
        cur += ch;
      }
    }
    cols.push(cur.trim());
    rows.push(cols);
  }
  return rows;
}

// ─── CSV Import Stepper ───────────────────────────────────────────────────────

interface CSVImportModalProps {
  open: boolean;
  account: BankAccountResponse | null;
  onClose: () => void;
  onImported: () => void;
}

const CSV_STEPS = [
  'Upload file',
  'Preview',
  'Map columns',
  'Categorise & submit',
];

const CSVImportModal = ({
  open,
  account,
  onClose,
  onImported,
}: CSVImportModalProps) => {
  const [step, setStep] = useState(0);
  const [rawRows, setRawRows] = useState<string[][]>([]);
  const [accountName, setAccountName] = useState(account?.name ?? '');
  const [accountType, setAccountType] = useState(account?.type ?? 'CURRENT');
  const [institution, setInstitution] = useState(account?.institution ?? '');
  const [colDate, setColDate] = useState(0);
  const [colAmount, setColAmount] = useState(1);
  const [colDesc, setColDesc] = useState(2);
  const [catMap, setCatMap] = useState<
    Record<string, { cat: string; dir: 'DEBIT' | 'CREDIT'; subCat: string }>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setStep(0);
      setRawRows([]);
      setAccountName(account?.name ?? '');
      setAccountType(account?.type ?? 'CURRENT');
      setInstitution(account?.institution ?? '');
      setColDate(0);
      setColAmount(1);
      setColDesc(2);
      setCatMap({});
      setError('');
    }
  }, [open, account]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const rows = parseCSV(text);
      if (rows.length < 2) {
        setError('File appears empty or unreadable.');
        return;
      }
      setRawRows(rows);
      setError('');
    };
    reader.readAsText(file);
  };

  const dataRows = rawRows.slice(1);
  const uniqueDescs = [
    ...new Set(dataRows.map((r) => r[colDesc] ?? '').filter(Boolean)),
  ];

  const handleCatChange = (
    desc: string,
    field: 'cat' | 'dir' | 'subCat',
    value: string,
  ) => {
    setCatMap((prev) => ({
      ...prev,
      [desc]: {
        ...{ cat: 'NEEDS', dir: 'DEBIT' as const, subCat: '' },
        ...(prev[desc] ?? {}),
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const rows: CSVImportRow[] = dataRows.map((r) => {
        const desc = r[colDesc] ?? '';
        const mapping = catMap[desc];
        const rawAmount = parseFloat(
          (r[colAmount] ?? '0').replace(/[£,\s]/g, ''),
        );
        return {
          date: r[colDate] ?? '',
          amount: Math.abs(rawAmount),
          description: desc,
          category: mapping?.cat ?? 'NEEDS',
          subCategory: mapping?.subCat || undefined,
        };
      });
      await importCSV({
        accountId: account?.id,
        accountName: accountName || undefined,
        accountType: accountType || undefined,
        institution: institution || undefined,
        rows,
      });
      onImported();
      onClose();
    } catch {
      setError('Import failed. Please check your file and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const colHeaders = rawRows[0] ?? [];
  const previewRows = rawRows.slice(1, 6);

  const canProceed = [rawRows.length > 1, true, true, !submitting][step];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import CSV</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3, mt: 1 }}>
          {CSV_STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Step 0 — File upload + account details */}
        {step === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              onClick={() => fileRef.current?.click()}
              sx={{
                border: `2px dashed ${alpha(C.blue, 0.3)}`,
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: C.blue,
                  bgcolor: alpha(C.blue, 0.03),
                },
              }}
            >
              <UploadFileOutlinedIcon
                sx={{ fontSize: 40, color: C.muted, mb: 1 }}
              />
              <Typography
                variant="body1"
                fontWeight={600}
                sx={{ color: C.ink }}
              >
                {rawRows.length > 0
                  ? `${rawRows.length - 1} rows loaded`
                  : 'Click to select a CSV file'}
              </Typography>
              <Typography variant="body2" sx={{ color: C.muted }}>
                Export your bank statement as CSV and upload it here
              </Typography>
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                hidden
                onChange={handleFile}
              />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Account type</InputLabel>
                  <Select
                    value={accountType}
                    label="Account type"
                    onChange={(e) =>
                      setAccountType(
                        e.target.value as BankAccountResponse['type'],
                      )
                    }
                  >
                    {Object.entries(ACCOUNT_TYPE_LABELS).map(([v, l]) => (
                      <MenuItem key={v} value={v}>
                        {l}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Account name</InputLabel>
                  <Select
                    value={accountName}
                    label="Account name"
                    onChange={(e) => setAccountName(e.target.value)}
                    renderValue={(v) => v || 'Type account name'}
                    displayEmpty
                  >
                    {account ? (
                      <MenuItem value={account.name}>{account.name}</MenuItem>
                    ) : null}
                    <MenuItem value="">
                      <em>Other / new account</em>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Bank / institution</InputLabel>
                  <Select
                    value={institution}
                    label="Bank / institution"
                    onChange={(e) => setInstitution(e.target.value)}
                    displayEmpty
                    renderValue={(v) => v || 'e.g. Barclays'}
                  >
                    {account ? (
                      <MenuItem value={account.institution}>
                        {account.institution}
                      </MenuItem>
                    ) : null}
                    <MenuItem value="">
                      <em>Other</em>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Step 1 — Preview */}
        {step === 1 && (
          <Box>
            <Typography variant="body2" sx={{ color: C.muted, mb: 1.5 }}>
              First 5 rows of your file — check the data looks correct before
              proceeding.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(C.blue, 0.04) }}>
                    {colHeaders.map((h, i) => (
                      <TableCell key={i} sx={{ fontWeight: 700 }}>
                        {h || `Col ${i + 1}`}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewRows.map((row, ri) => (
                    <TableRow key={ri}>
                      {row.map((cell, ci) => (
                        <TableCell key={ci}>{cell}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Step 2 — Column mapping */}
        {step === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" sx={{ color: C.muted }}>
              Tell us which column contains each field. Your file has{' '}
              {colHeaders.length} column(s).
            </Typography>
            {[
              { label: 'Date column', value: colDate, setter: setColDate },
              {
                label: 'Amount column',
                value: colAmount,
                setter: setColAmount,
              },
              {
                label: 'Description column',
                value: colDesc,
                setter: setColDesc,
              },
            ].map(({ label, value, setter }) => (
              <FormControl key={label} size="small" fullWidth>
                <InputLabel>{label}</InputLabel>
                <Select
                  value={value}
                  label={label}
                  onChange={(e) => setter(Number(e.target.value))}
                >
                  {colHeaders.map((h, i) => (
                    <MenuItem key={i} value={i}>
                      {h || `Column ${i + 1}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}
          </Box>
        )}

        {/* Step 3 — Categorise */}
        {step === 3 && (
          <Box>
            <Typography variant="body2" sx={{ color: C.muted, mb: 2 }}>
              Assign a category to each unique description. This applies to all
              rows with that description.
            </Typography>
            <TableContainer sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(C.blue, 0.04) }}>
                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Sub-category</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {uniqueDescs.map((desc) => {
                    const mapping = catMap[desc] ?? {
                      cat: 'NEEDS',
                      dir: 'DEBIT' as const,
                      subCat: '',
                    };
                    const catOptions = CATEGORIES[mapping.dir];
                    const selectedCat = catOptions.find(
                      (c) => c.value === mapping.cat,
                    );
                    return (
                      <TableRow key={desc}>
                        <TableCell
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {desc}
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            value={mapping.dir}
                            onChange={(e) =>
                              handleCatChange(desc, 'dir', e.target.value)
                            }
                            sx={{ minWidth: 90 }}
                          >
                            <MenuItem value="DEBIT">Expense</MenuItem>
                            <MenuItem value="CREDIT">Income</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            value={mapping.cat || catOptions[0]?.value}
                            onChange={(e) =>
                              handleCatChange(desc, 'cat', e.target.value)
                            }
                            sx={{ minWidth: 110 }}
                          >
                            {catOptions.map((c) => (
                              <MenuItem key={c.value} value={c.value}>
                                {c.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell>
                          {selectedCat && selectedCat.subCats.length > 0 ? (
                            <Select
                              size="small"
                              value={mapping.subCat || ''}
                              onChange={(e) =>
                                handleCatChange(desc, 'subCat', e.target.value)
                              }
                              sx={{ minWidth: 110 }}
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              {selectedCat.subCats.map((s) => (
                                <MenuItem key={s.value} value={s.value}>
                                  {s.label}
                                </MenuItem>
                              ))}
                            </Select>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        {step > 0 && (
          <Button onClick={() => setStep((s) => s - 1)} variant="outlined">
            Back
          </Button>
        )}
        {step < CSV_STEPS.length - 1 ? (
          <Button
            onClick={() => setStep((s) => s + 1)}
            variant="contained"
            disabled={!canProceed}
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? 'Importing…' : 'Import'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ─── Delete Confirm Dialog ─────────────────────────────────────────────────────

interface DeleteDialogProps {
  open: boolean;
  account: BankAccountResponse | null;
  onClose: () => void;
  onDeleted: () => void;
}

const DeleteDialog = ({
  open,
  account,
  onClose,
  onDeleted,
}: DeleteDialogProps) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    if (!account) return;
    setDeleting(true);
    setError('');
    try {
      await deleteBankAccount(account.id);
      onDeleted();
      onClose();
    } catch {
      setError('Could not delete account. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete account?</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography>
          This will permanently remove <strong>{account?.name}</strong> and all
          its transaction history. This cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={deleting}
        >
          {deleting ? 'Deleting…' : 'Delete account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Account Card ─────────────────────────────────────────────────────────────

interface AccountCardProps {
  account: BankAccountResponse;
  onImport: (a: BankAccountResponse) => void;
  onDelete: (a: BankAccountResponse) => void;
}

const AccountCard = ({ account, onImport, onDelete }: AccountCardProps) => {
  const typeColor = ACCOUNT_TYPE_COLORS[account.type] ?? C.blue;
  const syncInfo = SYNC_STATUS_LABELS[account.syncStatus] ?? {
    label: account.syncStatus,
    color: 'default',
  };

  const lastSyncedLabel = account.lastSynced
    ? new Date(account.lastSynced).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
    : 'Never';

  const maskedNumber = account.accountNumber
    ? `•••• ${account.accountNumber.slice(-4)}`
    : null;
  const formattedSort = account.sortCode
    ? account.sortCode.replace(/(\d{2})(\d{2})(\d{2})/, '$1-$2-$3')
    : null;

  return (
    <Card sx={{ height: '100%' }}>
      {/* Coloured header strip */}
      <Box
        sx={{ height: 6, bgcolor: typeColor, borderRadius: '16px 16px 0 0' }}
      />
      <CardContent sx={{ p: 2.5 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1,
          }}
        >
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ color: C.ink }}
            >
              {account.name}
            </Typography>
            <Typography variant="body2" sx={{ color: C.muted }}>
              {account.institution}
            </Typography>
          </Box>
          <Chip
            label={ACCOUNT_TYPE_LABELS[account.type] ?? account.type}
            size="small"
            sx={{
              bgcolor: alpha(typeColor, 0.12),
              color: typeColor,
              fontWeight: 600,
              fontSize: '0.7rem',
            }}
          />
        </Box>

        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ my: 1.5, color: C.ink }}
        >
          {fmt(account.balance)}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
          {maskedNumber && (
            <Typography variant="caption" sx={{ color: C.muted }}>
              {maskedNumber}
            </Typography>
          )}
          {formattedSort && (
            <Typography variant="caption" sx={{ color: C.muted }}>
              {formattedSort}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Chip
            label={syncInfo.label}
            color={syncInfo.color as any}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.68rem' }}
          />
          <Typography variant="caption" sx={{ color: C.muted }}>
            Last updated {lastSyncedLabel}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadFileOutlinedIcon />}
            onClick={() => onImport(account)}
            sx={{ flex: 1 }}
          >
            Import CSV
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => onDelete(account)}
          >
            Delete
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── BankAccountsPage ─────────────────────────────────────────────────────────

const BankAccountsPage = (): JSX.Element => {
  const [accounts, setAccounts] = useState<BankAccountResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [importTarget, setImportTarget] = useState<BankAccountResponse | null>(
    null,
  );
  const [csvOpen, setCsvOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BankAccountResponse | null>(
    null,
  );
  const [deleteOpen, setDeleteOpen] = useState(false);

  const loadAccounts = () => {
    setLoading(true);
    setError('');
    getBankAccounts()
      .then((res) => setAccounts(res.data))
      .catch(() => setError('Could not load bank accounts.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  const totalBalance = accounts
    .filter((a) => a.includedInBudget)
    .reduce((sum, a) => sum + a.balance, 0);

  const isStale =
    accounts.length === 0 ||
    accounts.some((a) => {
      if (!a.lastSynced) return true;
      return (Date.now() - new Date(a.lastSynced).getTime()) / 86_400_000 > 7;
    });

  const openImport = (account: BankAccountResponse) => {
    setImportTarget(account);
    setCsvOpen(true);
  };
  const openImportNew = () => {
    setImportTarget(null);
    setCsvOpen(true);
  };
  const openDelete = (account: BankAccountResponse) => {
    setDeleteTarget(account);
    setDeleteOpen(true);
  };

  return (
    <UserDashboard>
      <Box
        sx={{
          background: `linear-gradient(175deg, ${alpha(C.blue, 0.05)} 0%, transparent 40%)`,
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Container>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              mb: 3,
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <AccountBalanceOutlinedIcon
                sx={{ fontSize: 32, color: C.muted }}
              />
              <Box>
                <Typography variant="h4" fontWeight={700} sx={{ color: C.ink }}>
                  Bank Accounts
                </Typography>
                <Typography variant="body2" sx={{ color: C.muted }}>
                  Manage and import your account data
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<UploadFileOutlinedIcon />}
              onClick={openImportNew}
            >
              Import CSV
            </Button>
          </Box>

          {/* Stale data banner */}
          {!loading && isStale && (
            <Alert
              severity="warning"
              sx={{ mb: 3, borderRadius: 2 }}
              action={
                <Button color="inherit" size="small" onClick={openImportNew}>
                  Import now
                </Button>
              }
            >
              {accounts.length === 0
                ? 'No accounts yet — import a CSV bank statement to get started.'
                : 'Some accounts have not been updated recently. Import a fresh CSV to keep your data up to date.'}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {/* Total balance */}
          {!loading && accounts.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: C.muted,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Total Balance
                </Typography>
                <Typography
                  variant="h3"
                  fontWeight={700}
                  sx={{ mt: 0.5, color: C.ink }}
                >
                  {fmt(totalBalance)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, color: C.muted }}>
                  Across {accounts.filter((a) => a.includedInBudget).length}{' '}
                  accounts included in budget
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Accounts grid */}
          <Grid container spacing={3}>
            {loading ? (
              [1, 2, 3].map((i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton
                    variant="rectangular"
                    height={220}
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
              ))
            ) : accounts.length === 0 ? (
              <Grid item xs={12}>
                <Card>
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <AccountBalanceOutlinedIcon
                      sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }}
                    />
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      gutterBottom
                      sx={{ color: C.ink }}
                    >
                      No bank accounts yet
                    </Typography>
                    <Typography variant="body2" sx={{ color: C.muted, mb: 3 }}>
                      Import a CSV export from your bank to add an account and
                      start tracking your spending.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<UploadFileOutlinedIcon />}
                      onClick={openImportNew}
                    >
                      Import CSV
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ) : (
              accounts.map((account) => (
                <Grid item xs={12} sm={6} md={4} key={account.id}>
                  <AccountCard
                    account={account}
                    onImport={openImport}
                    onDelete={openDelete}
                  />
                </Grid>
              ))
            )}
          </Grid>
        </Container>
      </Box>

      <CSVImportModal
        open={csvOpen}
        account={importTarget}
        onClose={() => setCsvOpen(false)}
        onImported={loadAccounts}
      />
      <DeleteDialog
        open={deleteOpen}
        account={deleteTarget}
        onClose={() => setDeleteOpen(false)}
        onDeleted={loadAccounts}
      />
    </UserDashboard>
  );
};

export default BankAccountsPage;
